import React, { useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarCheck,
  faCalendarWeek,
  faHotel,
  faMap,
  faSearch,
  faUsers,
  faTicketAlt,
  faCrown,
  faDownload,
  faMoneyBill,
  faGear,
  faMusic,
  faSignInAlt,
  faBoxes,
  faUserTie,
  faMicrophone,
  faMapLocation
} from '@fortawesome/free-solid-svg-icons';
import styles from './LeftMenu.module.css';
import UserContext from '../context/UserContext';
import TourContext from '../context/TourContext';

const LoginPrompt = () => {
  const navigate = useNavigate();
  
  return (
    <div className={styles['login-prompt-container']}>
      <div className={styles['login-prompt']}>
        <div className={styles['login-icon']}>
          <FontAwesomeIcon icon={faSignInAlt} />
        </div>
        <h2>Please Sign In</h2>
        <p>You need to be logged in to view this content.</p>
        <button 
          className={styles['login-button']}
          onClick={() => navigate('/login')}
        >
          Log In / Sign Up
        </button>
      </div>
    </div>
  );
};

const LeftMenu = () => {
  const { userData } = useContext(UserContext);
  const { selectedTour, selectedDate, setCurrentSection } = useContext(TourContext);
  const navigate = useNavigate();
  const location = useLocation();

  const dateDependentSections = ['schedule', 'event', 'hotel', 'route'];

  const handleNavigate = (item) => {
    if (item.requiresDate && (!selectedTour || !selectedDate)) {
      alert('Please select a tour and date first');
      return;
    }
  
    if (item.section) {
      setCurrentSection(item.section);
    }
  
    // Special handling for schedule route
    if (item.path === '/schedule' && selectedTour && selectedDate) {
      navigate(`/tour/schedule/${selectedTour.id}/${selectedDate}`);
      return;
    }
  
    // Only update URL if we're not already there
    if (location.pathname !== item.path) {
      navigate(item.path);
    }
  };

  // Define all possible menu items
  const allMenuItems = {
    search: {
      label: 'Search',
      icon: faSearch,
      path: '/search',
      showWhen: () => true
    },
    crew: {
      label: 'Crew',
      icon: faUsers,
      path: '/crew',
      showWhen: () => true
    },
    routes: {
      label: 'Routes',
      icon: faMapLocation,
      path: '/routes-map',
      showWhen: () => true
    },
    myBands: {
      label: 'My Bands',
      icon: faMusic,
      path: '/my-bands',
      showWhen: () => true
    },
    tickets: {
      label: 'Tickets',
      icon: faTicketAlt,
      path: '/tickets',
      showWhen: () => true
    },
    expense: {
      label: 'Expense',
      icon: faMoneyBill,
      path: '/expense',
      showWhen: () => userData?.membershipTier !== 'Free'
    },
    membership: {
      label: 'Membership',
      icon: faCrown,
      path: '/membership',
      showWhen: () => true
    },
    downloads: {
      label: 'Downloads',
      icon: faDownload,
      path: '/downloads',
      showWhen: () => true
    },
    settings: {
      label: 'Settings',
      icon: faGear,
      path: '/settings',
      showWhen: () => true
    },
    inventory: {
      label: 'Inventory',
      icon: faBoxes,
      path: '/inventory',
      showWhen: () => true
    },
    managers: {
      label: 'Managers',
      icon: faUserTie,
      path: '/organization-managers',
      showWhen: () => true
    },
    acts: {
      label: 'Acts',
      icon: faMicrophone,
      path: '/managing-acts',
      showWhen: () => true
    },
    staff: {
      label: 'Staff',
      icon: faUsers,
      path: '/staff',
      showWhen: () => true
    },
    vendors: {
      label: 'Vendors',
      icon: faUsers,
      path: '/associated-vendors',
      showWhen: () => true
    },
    organizations: {
      label: 'Organizations',
      icon: faUsers,
      path: '/organizations',
      showWhen: () => true
    },
  };

  // Function to determine which menu items to show based on user type
  const getMenuItemsForUserType = () => {
    if (!userData) return [];
    
    const userType = userData.userType;
    
    switch(userType) {
      case 'Vendor':
        return [
          allMenuItems.search,
          allMenuItems.routes,
          allMenuItems.inventory,
          allMenuItems.staff,
       
          allMenuItems.membership,
          allMenuItems.downloads,
          allMenuItems.settings
        ];
      case 'Staff':
        return [
          allMenuItems.search,
          allMenuItems.routes,
          allMenuItems.vendors,
          allMenuItems.membership,
          allMenuItems.downloads,
          allMenuItems.settings
        ];
      case 'Organization':
        return [
          allMenuItems.search,
          allMenuItems.managers,
          allMenuItems.acts,
          allMenuItems.membership,
          allMenuItems.downloads,
          allMenuItems.settings
        ];
      case 'Artist':
      case 'Crew':
        case 'Manager':
      default:
        return [
          allMenuItems.search,
          allMenuItems.crew,
          allMenuItems.myBands,
          allMenuItems.routes,
          allMenuItems.tickets,
          allMenuItems.expense,
          allMenuItems.membership,
          allMenuItems.downloads,
          allMenuItems.settings
        ];
    }
  };

  const menuItems = getMenuItemsForUserType();

  return (
    <div className={styles['left-menu']}>
      {userData?.userSubType && (
        <div className={styles['subuser-type']}>
          {userData.userSubType}
        </div>
      )}

      <ul>
        {menuItems.map((item) => 
          item.showWhen() && (
            <li key={item.label}>
              <div 
                className={`${styles['menu-item']} ${
                  location.pathname === item.path ? styles.selected : ''
                } ${
                  item.requiresDate && (!selectedTour || !selectedDate) ? styles.disabled : ''
                }`}
                onClick={() => handleNavigate(item)}
              >
                <FontAwesomeIcon icon={item.icon} className={styles['menu-item-icon']} />
                <span>{item.label}</span>
              </div>
            </li>
          )
        )}
      </ul>
    </div>
  );
};

export { LeftMenu, LoginPrompt };
export default LeftMenu;